import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { BroadcastService } from '../../core/services/broadcast.service';
import { CachedCollection } from '../common/cached-collection';
import { CategoryItem } from '../categories/category-item.model';
import { CategoryMappingCsvUploadResult } from './category-mapping-csv-upload-result.model';
import { ImageAsset } from '../images/image-asset-model';
import { ImageManagerService } from '../images/image-manager.service';
import { LocalStorageManagerService } from '../../core/services/local-storage-manager.service';
import { Location } from '../location/location.model';
import { LocationEditModel } from '../location/location-edit.model';
import { MembershipType } from './membership-type-item.model';
import { MembershipTypeEditModel } from '../member/member-edit.model';
import { MyCampApp } from '../common/enumerations';
import { MyCampAppConstants } from '../common/constants';
import { Organization, TimeZone } from './organization.model';
import { OrganizationAdmin } from './organization-admin.model';
import { OrganizationAdminEditModel, OrganizationCreateModel } from './organization-create.model';
import { OrganizationAdminUpdateResult } from './organization-admin-update-result.model';
import { OrganizationApiService } from './organization-api.service';
import { OrganizationEditModel } from './organization-edit.model';
import { OrganizationLink } from '../link/organization-link.model';
import { OrganizationLinkEditModel } from '../link/organization-link-edit.model';
import { OrganizationListItem } from './organization-list-Item.model';
import { OrganizationPhotoLimitResult } from './organization-photo-limit.model';
import { RegistrationCode } from '../registration-code/regisration-code.model';
import { ServiceManagerBase } from '../common/service-manager-base';
import { ThemeManagerService } from '../theme/theme-manager.service';
import OrganizationDataSyncType = MyCampApp.OrganizationDataSyncType;
import { OrganizationRolloverComponent } from '../../sections/admin/organization-rollover/organization-rollover.component';
import { OrganizationRolloverModel } from './organization-rollover.model';

@Injectable({
    providedIn: 'root',
})
export class OrganizationManagerService extends ServiceManagerBase
{
    // Cache Containers
    private _organizationsCollection: CachedCollection<OrganizationListItem> | undefined = undefined;
    private _currentOrganization: Organization | undefined = undefined;

    constructor(private organizationApiService: OrganizationApiService,
                broadcastService: BroadcastService,
                private themeManager: ThemeManagerService,
                private imageManager: ImageManagerService,
                private localStorageManager: LocalStorageManagerService)
    {
        super(broadcastService);
        this.suppressOrganizationChanged = true;
    }

    get currentOrganization(): Observable<Organization>
    {
        if (this._currentOrganization) return of(this._currentOrganization);

        return this.organizationApiService.getCurrentOrganization().pipe(tap(org => this._currentOrganization = org));
    }

    get membershipTypeForChild(): Observable<number | undefined>
    {
        return this.currentOrganization.pipe(map(org =>
        {
            let membershipTypeForChild = org.MembershipTypes.find(mt => mt.Name.toLowerCase().indexOf('parent') !== -1)?.Id;
            if (membershipTypeForChild === undefined && org.DefaultMemberType != undefined)
                membershipTypeForChild = org.MembershipTypes.find(mt => mt.Name.indexOf(org.DefaultMemberType!) !== -1)?.Id;
            return membershipTypeForChild;
        }));
    }

    get isCampSite(): boolean { return this._currentOrganization !== undefined && this._currentOrganization.DataSyncType === OrganizationDataSyncType.CampSite; }

    getCurrentOrganizationImages(): Observable<Organization | undefined>
    {
        if (!this._currentOrganization || !this._currentOrganization.Logo || !this._currentOrganization.Photo || !this._currentOrganization.WelcomeImage)
            return of(this._currentOrganization);
        return this.organizationApiService.getCurrentOrganizationImages().pipe(map((json: any) =>
        {
            if (json.Logo) this._currentOrganization!.Logo = new ImageAsset(json.Logo);
            if (json.Photo) this._currentOrganization!.Photo = new ImageAsset(json.Photo);
            if (json.WelcomeImage) this._currentOrganization!.WelcomeImage = new ImageAsset(json.WelcomeImage);
            return this._currentOrganization;
        }));
    }

    switchingOrganizations()
    {
        this.localStorageManager.checkInCheckOutCategoryIds = [];
        this.clearCache();
    }

    clearCache()
    {
        this._currentOrganization = undefined;
    }

    getOrganizations(sortField?: string, sortOrder?: MyCampApp.SortOrder, pageIndex = 0, pageSize = MyCampAppConstants.defaultDataPageSize, bypassCache = false)
        : Observable<OrganizationListItem[]>
    {
        if (!bypassCache && this._organizationsCollection && !this._organizationsCollection.isExpired)
        {
            return of(this._organizationsCollection.collection);
        }

        return this.organizationApiService.getOrganizations(sortField, sortOrder, pageIndex, pageSize)
            .pipe(tap((items: OrganizationListItem[]) => this._organizationsCollection = new CachedCollection<OrganizationListItem>(items)));
    }

    getTimezonesList(): Observable<TimeZone[]>
    {
        return this.organizationApiService.getTimezonesList();
    }

    createOrganization(model: OrganizationCreateModel)
    {
        return this.organizationApiService.createOrganization(model).pipe(tap(json =>
        {
            this._organizationsCollection!.addItem(new OrganizationListItem(json));
            this.broadcastService.organizationListChanged.next(null);
        }));
    }

    getOrganizationAdmins(): Observable<OrganizationAdmin[]>
    {
        return this.organizationApiService.getOrganizationAdmins();
    }

    createOrganizationAdmin(model: OrganizationAdminEditModel): Observable<OrganizationAdminUpdateResult>
    {
        return this.organizationApiService.createOrganizationAdmin(model);
    }

    deleteOrganizationAdmin(id: number): Observable<any>
    {
        return this.organizationApiService.deleteOrganizationAdmin(id);
    }

    updateOrganizationAdmin(model: OrganizationAdminEditModel): Observable<OrganizationAdminUpdateResult>
    {
        return this.organizationApiService.updateOrganizationAdmin(model);
    }

    updateOrganizationAdminPassword(id: number, password: string): Observable<any>
    {
        return this.organizationApiService.updateOrganizationAdminPassword(id, password);
    }

    saveOrganization(model: OrganizationEditModel,
                     previousLogo?: ImageAsset, logoFile?: File, previousPhoto?: ImageAsset, photoFile?: File,
                     previousWelcomeImage?: ImageAsset, welcomeImageFile?: File): Observable<Organization>
    {
        return new Observable(observer =>
        {
            this.updateLogo(previousLogo, logoFile, model.LogoId)
                .pipe(take(1), switchMap(() => this.updatePhoto(previousPhoto, photoFile, model.PhotoId)))
                .pipe(take(1), switchMap(() => this.updateWelcomeImage(previousWelcomeImage, welcomeImageFile, model.WelcomeImageId)))
                .pipe(switchMap(() => this.organizationApiService.updateInfo(model)))
                .subscribe(org =>
                {
                    org.Logo = this._currentOrganization?.Logo;
                    org.Photo = this._currentOrganization?.Photo;
                    org.WelcomeImage = this._currentOrganization?.WelcomeImage;
                    this._currentOrganization = org;
                    observer.next(this._currentOrganization);
                    observer.complete();
                },
                error => observer.error(error));
        });
    }

    updateRestrictedSettings(org: Organization): Observable<boolean>
    {
        return this.organizationApiService.updateRestrictedSettings(org).pipe(tap(() => this.clearCache()));
    }

    setThemeForCurrentOrganization(themeId: number)
    {
        return this.organizationApiService.setTheme(themeId)
            .pipe(
                switchMap(() =>
                {
                    return this.themeManager.getThemes().pipe(tap(themes =>
                    {
                        this._currentOrganization!.Theme = themes.find(t => t.Id === themeId);
                    }));
                }));
    }

	rolloverOrganization(model: OrganizationRolloverModel): Observable<any>
	{
        return this.organizationApiService.rolloverOrganization(model);
	}

    private updateLogo(previousLogo?: ImageAsset, logoFile?: File, logoId?: number): Observable<boolean>
    {
        if (previousLogo != null && logoFile == null && logoId === -1) return this.organizationApiService.deleteLogo().pipe(tap(() => this._currentOrganization!.Logo = undefined));

        if (logoFile == null) return of(true);
        return this.imageManager.uploadFile(logoFile, true)
            .pipe(switchMap(result => this.organizationApiService.setLogo(result.fileOrUrl)
                .pipe(tap(json => this._currentOrganization!.Logo = new ImageAsset(json)), map(() => true))));
    }

    private updatePhoto(previousPhoto?: ImageAsset, photoFile?: File, photoId?: number): Observable<boolean>
    {
        if (previousPhoto != null && photoFile == null && photoId === -1)
            return this.organizationApiService.deletePhoto().pipe(tap(() => this._currentOrganization!.Photo = undefined));

        if (photoFile == null) return of(true);
        return this.imageManager.uploadFile(photoFile, true)
            .pipe(switchMap(result => this.organizationApiService.setPhoto(result.fileOrUrl)
                .pipe(tap(json => this._currentOrganization!.Photo = new ImageAsset(json)), map(() => true))));
    }

    private updateWelcomeImage(previousWelcomeImage?: ImageAsset, welcomeImageFile?: File, welcomeImageId?: number): Observable<boolean>
    {
        if (previousWelcomeImage != null && welcomeImageFile == null && welcomeImageId === -1)
            return this.organizationApiService.deleteWelcomeImage().pipe(tap(() => this._currentOrganization!.WelcomeImage = undefined));

        if (welcomeImageFile == null) return of(true);
        return this.imageManager.uploadFile(welcomeImageFile)
            .pipe(switchMap(result => this.organizationApiService.setWelcomeImage(result.fileOrUrl)
                .pipe(tap(json => this._currentOrganization!.WelcomeImage = new ImageAsset(json)), map(() => true))));
    }

    saveMembershipType(model: MembershipTypeEditModel): Observable<MembershipType>
    {
        if (model.Id === 0)
        {
            return this.organizationApiService.createMembershipType(model)
                .pipe(
                    map(newMemberType =>
                    {
                        model.Id = newMemberType.Id;
                        const memberType = new MembershipType(model);
                        memberType.CategoryItems = this._currentOrganization!.CategoryItems.filter(ci => model.CategoryIds.some(id => id === ci.Id));
                        this._currentOrganization!.MembershipTypes = [...this._currentOrganization!.MembershipTypes, memberType];
                        return memberType;
                    }));
        }

        return this.organizationApiService.updateMembershipType(model)
            .pipe(
                map(() =>
                {
                    const memberType: MembershipType = this._currentOrganization!.MembershipTypes.find(mt => mt.Id === model.Id)!;
                    memberType.Name = model.Name;
                    memberType.CanUploadMedia = model.CanUploadMedia;
                    memberType.CanDeleteMedia = model.CanDeleteMedia;
                    memberType.UploadAlbumId = model.UploadAlbumId;
                    memberType.CategoryItems = this._currentOrganization!.CategoryItems.filter(ci => model.CategoryIds.some(id => id === ci.Id));
                    this.categoryOrMemberTypeUpdated();
                    return memberType;
                }));
    }

    deleteMembershipType(id: number): Observable<any>
    {
        return this.organizationApiService.deleteMembershipType(id).pipe(tap(() =>
        {
            this._currentOrganization!.MembershipTypes.splice(this._currentOrganization!.MembershipTypes.findIndex(mt => mt.Id === id), 1);
        }));
    }

    checkPhotoLimit(sendEmail: boolean): Observable<OrganizationPhotoLimitResult>
    {
        return this.organizationApiService.checkPhotoLimit(sendEmail);
    }

    categoryCreated(category: CategoryItem)
    {
        this._currentOrganization!.CategoryItems.push(category);
        this._currentOrganization!.CategoryItems.sort((cat1, cat2) => cat1.Name.localeCompare(cat2.Name));
        this._currentOrganization!.MembershipTypes.filter(mt => category.MemberTypeIds.find(mtId => mt.Id === mtId) != null)
            .forEach(mt => {
                mt.CategoryItems.push(category);
                mt.CategoryItems.sort((cat1, cat2) => cat1.Name.localeCompare(cat2.Name));
            });
    }

    categoryOrMemberTypeUpdated(): void
    {
        // Too many things might have happened with categories and member types. Let's reload
        this._currentOrganization = undefined;
        this.currentOrganization.subscribe();
    }

    categoryDeleted(category: CategoryItem)
    {
        this._currentOrganization!.CategoryItems.splice(this._currentOrganization!.CategoryItems.findIndex(ci => ci.Id === category.Id), 1);
        this._currentOrganization!.MembershipTypes.filter(mt => category.MemberTypeIds.find(mtId => mt.Id === mtId) != null)
            .forEach(mt =>
            {
                const categoryIndex = mt.CategoryItems.findIndex(ci => ci.Id === category.Id);
                if (categoryIndex !== -1)
                {
                    mt.CategoryItems.splice(categoryIndex, 1);
                }
            });

        this.categoryOrMemberTypeUpdated();
    }

    locationCreated(location: Location)
    {
        this._currentOrganization!.Locations.push(location);
    }

    locationUpdated(model: LocationEditModel): Location
    {
        const location = this._currentOrganization!.Locations.find(l => l.Id === model.Id)!;
        location.updateFromModel(model);
        return location;
    }

    locationDeleted(location: Location)
    {
        this._currentOrganization!.Locations.splice(this._currentOrganization!.Locations.findIndex(l => l.Id === location.Id), 1);
    }

    linkCreated(link: OrganizationLink)
    {
        this._currentOrganization!.Links.push(link);
    }

    linkUpdated(model: OrganizationLinkEditModel): OrganizationLink
    {
        const link = this._currentOrganization!.Links.find(l => l.Id === model.Id)!;
        Object.assign(link, model);
        return link;
    }

    linkDeleted(link: OrganizationLink)
    {
        this._currentOrganization!.Links.splice(this._currentOrganization!.Links.findIndex(rc => rc.Id === link.Id), 1);
    }

    registrationCodeCreated(registrationCode: RegistrationCode)
    {
        this._currentOrganization!.RegistrationCodes.push(registrationCode);
    }

    registrationCodeUpdated(model: RegistrationCode): RegistrationCode
    {
        const registrationCode = this._currentOrganization!.RegistrationCodes.find(rc => rc.Id === model.Id)!;
        Object.assign(registrationCode, model);
        return registrationCode;
    }

    registrationCodeDeleted(registrationCode: RegistrationCode)
    {
        this._currentOrganization!.RegistrationCodes.splice(this._currentOrganization!.RegistrationCodes.findIndex(rc => rc.Id === registrationCode.Id), 1);
    }

    createBaseMappingFileCsv(file: File): Observable<CategoryMappingCsvUploadResult>
    {
        return this.organizationApiService.createBaseMappingFileCsv(file);
    }

    uploadCategoriesMappingCsv(file: File): Observable<CategoryMappingCsvUploadResult>
    {
        return this.organizationApiService.uploadCategoriesMappingCsv(file);
    }

    getCurrentMappingFile(): Observable<any>
    {
        return this.organizationApiService.getCurrentMappingFile();
    }

	getDisplayedCategoryNameList(categoryIds: number[])
	{
		if (this._currentOrganization === undefined || categoryIds == null || categoryIds.length === 0) return '';
		const org = this._currentOrganization;

		// Sort order: by Year, then by CategoryItemType (.Session, .Group, .Bus, .Location, .Other) then by Name. Items that have parent categories are
		// displayed immediately after their parent categories.
		// First, filter and sort by Year, CategoryItemType, and Name
		const sortedCategories = org.CategoryItems
			.filter(ci => categoryIds.some(id => id === ci.Id))
			.sort((a, b) => {
				// First, sort by year
				if (a.Year !== b.Year) return a.Year - b.Year;

				// Then by CategoryItemType
				const typeComparison = MyCampApp.isCategoryItemTypeSortedBefore(a.CategoryItemType, b.CategoryItemType);
				if (typeComparison !== 0) return typeComparison;

				// Finally by name
				return a.Name.localeCompare(b.Name);
			});

		// Second, reorganize to place children immediately after their parents
		const categoriesToDisplay: CategoryItem[] = [];
		const processed = new Set<number>();

		for (const category of sortedCategories) {
			if (processed.has(category.Id)) continue;

			// Add the category (either parent or standalone)
			categoriesToDisplay.push(category);
			processed.add(category.Id);

			// Find and add all children of this category
			const children = sortedCategories.filter(c =>
				!processed.has(c.Id) && c.ParentId === category.Id
			);

			for (const child of children) {
				categoriesToDisplay.push(child);
				processed.add(child.Id);
			}
		}
		
		return categoriesToDisplay.map(ci => (org.EnrollmentYear === 0 ? '' : `[${ci.Year}] `) + ci.Name).join(', ');
	}
}
